import { css } from 'aphrodite';
import React, { useEffect, useRef, useState } from 'react';

import { orgsApi } from '@/features/org/api/orgs.api';
import { orgStyles as s } from '@/features/org/components/org.styles';
import { useOrgs } from '@/features/org/hooks/use-orgs/useOrgs';
import { useParams, useHistory } from '@/shared/hooks/use-router/react-router-hooks';

/**
 * /invite/:token — the redeem landing.
 *
 * Two phases:
 *   1. Verify the user is signed in. If not, bounce to /login with
 *      `?next=/invite/:token` so the auth flow brings them back.
 *   2. POST /api/invites/:token/redeem. On success, refresh orgs,
 *      switch to the new org, and navigate to its dashboard.
 *
 * Redeem is single-shot per token: an invite is never redeemed twice,
 * StrictMode double-invokes included.
 */
export default function RedeemInvitePage() {
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const { switchOrg, refresh } = useOrgs();

  const [status, setStatus] = useState<'idle' | 'working' | 'success' | 'error'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const ran = useRef(false);
  // Unmount guard, kept apart from the redeem effect: its setup re-arms it, so
  // StrictMode's mount → unmount → mount leaves it true.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (ran.current) return; // guard against React 18 StrictMode double-mount
    ran.current = true;

    (async () => {
      setStatus('working');
      try {
        const res = await orgsApi.invites.redeem(token);
        if (!mounted.current) return;
        setOrgName(res.org?.name ?? null);
        if (res.org?.slug) {
          await refresh();
          switchOrg(res.org.slug);
          setStatus('success');
          // Auto-jump after a brief confirmation.
          setTimeout(() => history.push(`/o/${res.org!.slug}/dashboard`), 1200);
          return;
        }
        setStatus('success');
      } catch (e: any) {
        if (!mounted.current) return;
        if (e?.status === 401) {
          // Not logged in — bounce to login and remember where to return.
          const next = encodeURIComponent(`/invite/${token}`);
          history.replace(`/login?next=${next}`);
          return;
        }
        setErr(e.message || 'Invite could not be redeemed.');
        setStatus('error');
      }
    })();
    // Runs once per token. `refresh` / `switchOrg` / `history` are NOT deps:
    // useOrgs() returns fresh closures every render, so listing them re-ran this
    // effect on the first re-render — its cleanup cancelled the in-flight redeem
    // and the `ran` guard stopped the re-run, leaving the page on "Redeeming…".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className={css(s.page)} style={{ maxWidth: 480 }}>
      <div className={css(s.pageTitle)} style={{ marginBottom: 12 }}>
        Redeem invite
      </div>
      {status === 'working' && <div className={css(s.empty)}>Redeeming…</div>}
      {status === 'success' && (
        <div className={css(s.empty)}>✓ You've joined {orgName ?? 'the org'}. Redirecting…</div>
      )}
      {status === 'error' && (
        <>
          <div className={css(s.errorBox)}>{err}</div>
          <button className={css(s.btnGhost)} onClick={() => history.push('/dashboard')}>
            Back to dashboard
          </button>
        </>
      )}
    </div>
  );
}
